import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import classNames from 'classnames';
import styles from '@app/pages/analytics/project-settings/analytics-main-settings/styles.module.scss';
import Icon from '@app/components/ui/icons';
import {
  Button,
  CircleButton,
  EasyModal,
  Input,
  Loading,
  PermissionAccessWrapper,
  Select,
} from '@ui';
import {
  AllStatusProject,
  AnalyticProjectExportSettings,
  CreateProjectAnalyticsParams,
  FinalImportType,
  GroupSharedStatus,
  ProjectItem,
} from '@app/interfaces/analytics';
import { userPermissions } from '@app/utils/user-permissions';
import { Controller, useForm } from 'react-hook-form';
import useTranslation from '@app/hooks/use-translation';
import { AutoFocusHook } from '@app/utils/helpers';
import { navigationRoutes } from '@app/utils/navigation-routes';
import { useNavigate } from 'react-router-dom';
import {
  useCreateSingleAnalyticProjectMutation,
  useDeleteAnalyticProjectMutation,
  useEditSingleAnalyticProjectMutation,
  useEditSingleAnalyticProjectOwnerMutation,
  useGetAnalyticsProjectsListQuery,
  useGetSingleAnalyticProjectQuery,
  useLazyExportAnalyticProjectSettingsQuery,
  useLazyImportAnalyticProjectSettingsQuery,
} from '@app/store/api/analytics-projects.api';
import { OptionItem } from '@app/components/ui/select/select.type';
import { useGetExtAndUsrModelsQuery } from '@app/store/api/asr_model.api';
import { useGetRecognitionDefaultModelQuery } from '@app/store/api/recognition-rules-settings-page.api';
import { useGetUserGroupsBulkQuery } from '@app/store/api/users.api';
import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';
import { usePermissionCheck } from '@app/hooks/use-permission';
import {
  ExportFormFeature,
  ImportAPFeature,
} from '@app/components/analytic-project-import-export-features';
import {
  useGetAnalyticsProjectPresetQuery,
  useGetChatAnalyticsProjectPresetQuery,
} from '@app/store/api/analytics-project-preset.api';
import { toast } from 'react-toastify';
type MainSettingsPropsType = {
  newProjFlag?: boolean;
};
const MainSettings: FC<MainSettingsPropsType> = (props) => {
  const { newProjFlag = false } = props;
  const [deleteAnalyticProject] = useDeleteAnalyticProjectMutation();
  const { status, currentTemplate, project } =
    useOutletContext<AnalyticPageOutlet | undefined>() || {};
  const { id } = useParams();

  // preset api
  const { data: analyticsProjectPresetVoice } = useGetAnalyticsProjectPresetQuery(
    {
      project_id: project?.project_id || '',
    },
    { skip: !project?.project_id || currentTemplate === 'chat' },
  );
  const { data: analyticsProjectPresetChat } = useGetChatAnalyticsProjectPresetQuery(
    {
      project_id: project?.project_id || '',
    },
    { skip: !project?.project_id || currentTemplate === 'voice' },
  );
  const analyticsProjectPreset = useMemo(
    () =>
      ({
        chat: analyticsProjectPresetChat,
        voice: analyticsProjectPresetVoice,
      }[currentTemplate || 'voice']),
    [analyticsProjectPresetChat, analyticsProjectPresetVoice, currentTemplate],
  );
  // preset api

  // IMPORT export api
  const [exportModalState, changeExportModalState] = useState(false);
  const [importModalState, changeImportModalState] = useState(false);
  const [exportSettings, changeExportSettings] = useState<
    Omit<AnalyticProjectExportSettings, 'project_id'>
  >({
    include_metrics: false,
    include_preset: false,
    include_reports: false,
  });
  const [exportAPSettings] = useLazyExportAnalyticProjectSettingsQuery();
  const [importAPSettings] = useLazyImportAnalyticProjectSettingsQuery();

  function handleExportAPSettings() {
    if (!id) return;
    const result: AnalyticProjectExportSettings = { ...exportSettings, project_id: id };
    exportAPSettings(result)
      .unwrap()
      .then(() => {
        toast.success(t('export_success'));
      })
      .catch(() => {
        toast.error(t('export_error'));
      });
  }

  function handleImport(data: FinalImportType) {
    if (!id) return;

    importAPSettings({ params: { project_id: id }, body: data })
      .unwrap()
      .then(() => {
        toast.success(t('import_success'));

        setTimeout(() => {
          window.location.reload();
        }, 1500);
      })
      .catch(() => {
        toast.error(t('import_error'));
      });
  }

  // IMPORT export api
  const actionGetGroups = usePermissionCheck({ tag: 'actionGetGroups', permissions: 'action' });

  const { t } = useTranslation('pages.mainSettingsAp');
  const { t: ts } = useTranslation('common.sharedStatus');
  const { t: form } = useTranslation('common.forms');
  const { t: tf } = useTranslation('pages.metricTemplates');
  const navigate = useNavigate();

  const inputFocusRef = AutoFocusHook();

  const defaultAsrModelPermission = usePermissionCheck({
    permissions: 'action',
    tag: userPermissions.action.actionGetDefaultASRModel,
  });

  const { data: groupsList } = useGetUserGroupsBulkQuery(undefined, {
    skip: Boolean(actionGetGroups),
  });
  const {
    data: projectData,
    isFetching,
    isLoading,
  } = useGetSingleAnalyticProjectQuery(
    {
      id: String(id || 0),
    },
    { skip: typeof id === undefined || newProjFlag, refetchOnMountOrArgChange: true },
  );
  const isLoadingFormData = isFetching || isLoading;
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { data: default_model } = useGetRecognitionDefaultModelQuery(undefined, {
    skip: !!defaultAsrModelPermission,
  });

  const { data: analyticGroups, isLoading: analyticsGroupsLoading } =
    useGetAnalyticsProjectsListQuery();
  const { data: models, isLoading: modelsLoading } = useGetExtAndUsrModelsQuery();
  const [createAnalyticsProject, { isLoading: createLoading }] =
    useCreateSingleAnalyticProjectMutation();
  const [editAnalyticsProject, { isLoading: editLoading }] = useEditSingleAnalyticProjectMutation();
  const [editProjectOwner] = useEditSingleAnalyticProjectOwnerMutation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    clearErrors,
  } = useForm<Omit<CreateProjectAnalyticsParams, 'id'>>({ defaultValues: { user_groups: [] } });

  const copyToClipboard = useCallback(() => {
    event?.preventDefault();
    const textToCopy = projectData?.project_id;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    }
  }, [projectData?.project_id]);

  useEffect(() => {
    projectData &&
      reset({
        name: projectData?.name,
        project_group_id: projectData?.project_group_id,
        shared: projectData?.shared,
        timezone: projectData?.timezone,
        description: ' ',
        asr_model_id: projectData?.asr_model_id,
        user_groups: projectData?.user_groups || [],
      });
  }, [projectData, reset]);

  useEffect(() => {
    if (!newProjFlag) return;
    if (!models) return;
    setValue(
      'asr_model_id',
      default_model?.model_id || models?.find((item) => item.default)?.model_id || '',
    );
  }, [default_model?.model_id, models, newProjFlag, setValue]);

  function handleBeOwnerClick() {
    editProjectOwner({ params: { id: id || '' } });
  }
  function onSubmit(data: CreateProjectAnalyticsParams) {
    !newProjFlag
      ? projectData?.project_id &&
        editAnalyticsProject({ body: data, params: { id: projectData?.project_id } }).then(() => {
          navigate(`/${navigationRoutes.analytics}`);
        })
      : createAnalyticsProject({ ...data, description: data.name }).then((data) => {
          const { data: projectData } = data as { data: ProjectItem };
          if (projectData?.project_id) {
            navigate(`/${navigationRoutes.analytics}/${projectData.project_id}`);
          }
        });
  }
  const disableStatusForSubmit = () => {
    if (createLoading) return true;
    if (editLoading) return true;
    if (!newProjFlag) {
      if (!projectData?.owner && projectData?.shared === GroupSharedStatus.VIEW) return true;
      if (status?.project_status === AllStatusProject.PAUSE) {
        return false;
      }
      return status?.project_status !== AllStatusProject.CREATED;
    }
  };
  const disableStatusForButton = () => {
    if (!newProjFlag) {
      return status?.project_status !== 'PAUSE';
    } else return false;
  };
  const deleteProjectClick = () => {
    if (newProjFlag) {
      navigate(`/${navigationRoutes.analytics}`);
    } else {
      setDeleteModal(true);
    }
  };
  function getSelectOptions() {
    const newArr: OptionItem[] = [];
    const values = Object.values(GroupSharedStatus).filter((i) => isNaN(Number(i)));
    for (let i = 0; i < values.length; i++) {
      if (actionGetGroups) {
        if (
          values[i] === GroupSharedStatus.EDITGROUP ||
          values[i] === GroupSharedStatus.VIEWGROUP
        ) {
          continue;
        }
      }
      newArr.push({
        value: values[i],
        title: ts(values[i]),
      });
    }
    return newArr;
  }
  const getModelNameById = (id: string) => {
    return models?.find((item) => item.model_id === id)?.name || 'N/A';
  };
  const UTCOptions = useMemo(
    () => [
      { value: 'default', title: t('system.timezone_default') },
      ...new Array(24).fill('_').map<OptionItem>((_, index) => {
        const currentIndex = ++index;
        const currentValue = Number(currentIndex > 12 ? currentIndex - 25 : currentIndex);
        return {
          value: Number(currentValue),
          title: `UTC${currentValue > 0 ? '+' : ''}${currentValue}`,
        };
      }),
    ],
    [t],
  );
  function onSuccessModelChange() {
    setModalVisible(false);
  }
  function deleteModalApply() {
    projectData?.project_id && deleteAnalyticProject({ id: projectData?.project_id });
    navigate(`/${navigationRoutes.analytics}`);
  }
  const groupsOptions: OptionItem[] =
    groupsList?.groups?.map((group) => ({ title: group.name, value: group.id })) || [];

  if (analyticsGroupsLoading || modelsLoading || isLoadingFormData) return <Loading />;
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ minHeight: '594px', display: 'flex', flexDirection: 'column' }}>
          <div className={classNames(styles.main)}>
            <div className={classNames(styles.folderIcon)}>
              <Icon size={70} className="text-speech_analitics" name={'FolderOpenIcon'} />
            </div>
            <div className={classNames(styles.content)}>
              <h4 className="mb-[27px] text-0color text-[18px] leading-[21px]">
                <b>{t('main_settings_title')}</b>
              </h4>
              <div className="flex items-center mb-[23px] w-[550px]">
                <div className="flex items-center w-full justify-between">
                  <span className="w-full">{t('form_fields.proj_name')}</span>
                  <div className="min-w-[346px]">
                    <Input
                      {...inputFocusRef}
                      {...register('name', {
                        required: { value: true, message: form('required') },
                      })}
                      disabled={
                        !projectData?.owner && projectData?.shared === GroupSharedStatus.VIEW
                      }
                      error={errors.name?.message}
                    />
                  </div>
                </div>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', marginBottom: '23px' }}
                className="w-[550px] justify-between"
              >
                <label className={classNames(styles.first, 'text-gray-700')} htmlFor="group">
                  {t('form_fields.group_proj')}
                </label>
                <div className="w-[346px]">
                  <Controller
                    render={() => (
                      <Select
                        disable={
                          !projectData?.owner && projectData?.shared === GroupSharedStatus.VIEW
                        }
                        error={errors.project_group_id?.message}
                        dropHeight={127}
                        dropWidth={342}
                        options={
                          analyticGroups?.map((e) => {
                            return { title: e.name, value: e.project_group_id };
                          }) || []
                        }
                        defaultValue={watch('project_group_id')}
                        onChange={(value) => {
                          setValue('project_group_id', String(value));
                          clearErrors('project_group_id');
                        }}
                        placeholder={newProjFlag ? t('system.placeholder') + '...' : undefined}
                      />
                    )}
                    name={'project_group_id'}
                    control={control}
                    rules={{ validate: (value) => !!value || form('required') }}
                  />
                </div>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center' }}
                className="justify-between w-[550px]"
              >
                <label className={classNames(styles.four, 'text-gray-700')} htmlFor="group">
                  {t('form_fields.proj_shared')}
                </label>
                <div className="w-[346px]">
                  <Controller
                    render={() => (
                      <Select
                        disable={!newProjFlag ? !projectData?.owner : false}
                        dropHeight={127}
                        dropWidth={342}
                        error={errors.shared?.message}
                        options={getSelectOptions()}
                        defaultValue={watch('shared')}
                        onChange={(item) => {
                          if (item === GroupSharedStatus.PRIVATE) {
                            setValue('user_groups', []);
                          }
                          setValue('shared', String(item) as GroupSharedStatus);
                          clearErrors('shared');
                        }}
                        placeholder={newProjFlag ? t('system.placeholder') + '...' : undefined}
                      />
                    )}
                    name={'shared'}
                    control={control}
                    rules={{ validate: (value) => !!value || form('required') }}
                  />
                </div>
              </div>
              {projectData?.owner !== false &&
                (watch(`shared`) === GroupSharedStatus.EDITGROUP ||
                  watch('shared') === GroupSharedStatus.VIEWGROUP) && (
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    className="justify-between w-[550px] pt-[20px]"
                  >
                    <label
                      className={classNames('', 'text-gray-700 whitespace-nowrap')}
                      htmlFor="group"
                    >
                      {t('form_fields.groups')}
                    </label>
                    <div className="w-[346px]">
                      <Select
                        options={groupsOptions}
                        isMulti
                        defaultValue={watch('user_groups')}
                        onChangeMultiple={(val) => setValue('user_groups', val as string[])}
                      />
                    </div>
                  </div>
                )}
              <div className="flex items-center mt-[23px] justify-between w-[550px]">
                <label className={classNames(styles.third, 'text-gray-700')} htmlFor="model">
                  {t('form_fields.timezone')}
                </label>
                <div className="w-[346px] flex justify-end">
                  <Controller
                    render={() => (
                      <Select
                        dropHeight={127}
                        dropWidth={342}
                        defaultValue={Number(watch('timezone')) || 'default'}
                        options={UTCOptions}
                        onChange={(item) => {
                          setValue('timezone', item === 'default' ? undefined : Number(item));
                          clearErrors('timezone');
                        }}
                      />
                    )}
                    name={'timezone'}
                    control={control}
                  />
                </div>
              </div>
              <PermissionAccessWrapper
                tag={userPermissions.display.displayAPModel}
                permissions={'display'}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', marginTop: '23px' }}
                  className=" gap-[19px]"
                >
                  <label className={classNames(styles.fifth, 'text-gray-700')} htmlFor="model">
                    {t('form_fields.recog_model')}
                  </label>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    className="self-start"
                  >
                    <span className="w-full">{getModelNameById(watch('asr_model_id'))}</span>
                    {!(!projectData?.owner && projectData?.shared === GroupSharedStatus.VIEW) && (
                      <div className="flex items-center gap-[10px]">
                        <CircleButton
                          hintTitle={t('popup_hints.recog_model_button')}
                          size={16}
                          className="text-action w-[30px] mt-[6px]"
                          icon="EditIcon"
                          onClick={() => setModalVisible(true)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </PermissionAccessWrapper>
              <div className="flex items-center justify-between w-[550px] gap-[115px] mt-[23px]">
                <span className="text-gray-700 text-[16px]">{tf('project_id')}</span>
                <div className="flex gap-2">
                  <p>{projectData?.project_id}</p>
                  <CircleButton
                    onClick={copyToClipboard}
                    icon="CopyIcon"
                    className="text-action mb-[5px]"
                    hintTitle={tf('copy_hint')}
                    size={16}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classNames(styles.footer)}>
            <div className="flex items-center justify-between h-full py-6 px-6">
              <div className="flex gap-[20px]">
                <Button
                  disabled={disableStatusForSubmit()}
                  type="submit"
                  label={newProjFlag ? t('buttons.create_project') : t('buttons.save_project')}
                />
              </div>
              <div className="flex items-center gap-[10px]">
                {projectData?.status === 'CREATED' && analyticsProjectPreset?.preset === null && (
                  <Button
                    onClick={() => changeImportModalState(true)}
                    label={t('import_ap_settings_button_label')}
                    fill="outlined"
                  />
                )}
                {projectData && (
                  <Button
                    onClick={() => changeExportModalState(true)}
                    label={t('download_ap_settings_button_label')}
                    fill="outlined"
                  />
                )}
                {(projectData?.shared === 'EDIT' || projectData?.shared === 'EDITGROUP') &&
                  !projectData?.owner && (
                    <Button
                      onClick={handleBeOwnerClick}
                      fill="outlined"
                      label={t('buttons.be_owner')}
                    />
                  )}
                <Button
                  disabled={disableStatusForButton()}
                  variant="danger"
                  fill="outlined"
                  icon="TrashIcon"
                  onClick={() => deleteProjectClick()}
                  label={t('buttons.delete_project')}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
      {/*modals  */}
      {modalVisible && (
        <EasyModal
          variant={'medium484'}
          label={<div className="m-[0px_auto] text-center">{t('recog_model_modal_title')}</div>}
          onSuccess={() => onSuccessModelChange()}
          show={modalVisible}
          isAutoClosable={false}
          onClose={() => {
            setModalVisible(false);
            setValue('asr_model_id', projectData?.asr_model_id || '');
          }}
        >
          <div className="w-[340px] m-[0px_auto] pt-[10px] pb-[150px]">
            <Select
              dropHeight={100}
              dropWidth={336}
              options={[
                ...(models?.map((item) => ({ title: item.name, value: item.model_id })) || []),
              ]}
              onChange={(value) => setValue('asr_model_id', String(value))}
              defaultValue={watch('asr_model_id')}
            />
          </div>
        </EasyModal>
      )}
      <EasyModal
        variant={'removal'}
        show={deleteModal}
        onClose={setDeleteModal.bind(null, false)}
        label={`${t('delete_proj')} "${projectData?.name}"`}
        onRemove={deleteModalApply}
      >
        <p className="px-[15px] flex justify-center">{t('delete_proj_confirm')}</p>
      </EasyModal>
      {/* export modal */}
      <EasyModal
        show={exportModalState}
        onClose={() => changeExportModalState(false)}
        variant="medium484"
        label={t('export_modal_label')}
        withoutFooter
      >
        {exportModalState && (
          <ExportFormFeature
            changeExportSettings={changeExportSettings}
            exportSettings={exportSettings}
            handleExportAPSettings={handleExportAPSettings}
            onClose={() => changeExportModalState(false)}
          />
        )}
      </EasyModal>
      {/* import Modal */}
      <EasyModal
        variant="large"
        show={importModalState}
        onClose={() => changeImportModalState(false)}
        withoutFooter
        withoutLabel
      >
        {importModalState && (
          <ImportAPFeature
            onClose={() => changeImportModalState(false)}
            handleImport={handleImport}
            template={currentTemplate || 'voice'}
          />
        )}
      </EasyModal>
      {/*modals  */}
    </>
  );
};

export default MainSettings;
